"use client"

/**
 * DotPattern — dot grid revealed by a soft drifting "cloud" mask.
 *
 * Inspiration: Google/Apple AI loading states — a diffuse dot field that
 * softly fades in, drifts diagonally across the surface, then fades out.
 * No bright glow cores, no particles — just one or two large soft halos
 * sliding across the grid so the dots appear as an ambient cloud.
 */

import * as React from "react"
import { LazyMotion, domAnimation, m } from "motion/react"
import { cn } from "../../lib/utils"

interface DotPatternProps extends React.SVGProps<SVGSVGElement> {
  width?: number
  height?: number
  x?: number
  y?: number
  cx?: number
  cy?: number
  cr?: number
  className?: string
  glow?: boolean
  /** Number of drifting soft clouds (keep small: 1–2). */
  glowCount?: number
  /** Cloud radius — large values produce a wide, diffuse reveal. */
  glowRadius?: number
}

type Cloud = {
  key: number
  xs: string[]
  ys: string[]
  duration: number
  delay: number
}

/**
 * Tiny deterministic PRNG (mulberry32). We use a seeded RNG instead of
 * `Math.random()` so the SVG attributes emitted on the server match the
 * client's first paint — otherwise React reports a hydration mismatch and
 * has to re-paint every drifting `<motion.circle>` on mount, which is both
 * a perf cost and a visible jump.
 */
function mulberry32(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s + 0x6d2b79f5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function hashString(str: string): number {
  // Cheap FNV-1a-style hash. Stable across SSR + CSR for the same input.
  let h = 2166136261
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

export function DotPattern({
  width = 14,
  height = 14,
  x = 0,
  y = 0,
  cx = 1,
  cy = 1,
  cr = 0.8,
  className,
  glow = false,
  glowCount = 2,
  glowRadius = 240,
  ...props
}: DotPatternProps) {
  const id = React.useId()
  const maskId = `${id}-mask`
  const gradId = `${id}-grad`

  const clouds = React.useMemo<Cloud[]>(() => {
    const rng = mulberry32(hashString(`${id}|${glowCount}`))
    const rand = (min: number, max: number) => min + rng() * (max - min)
    return Array.from({ length: glowCount }).map((_, i) => {
      // Drift diagonally: bottom-right → top-left. Start/end partly off-canvas
      // so the cloud enters and exits softly without a visible edge.
      const startX = rand(85, 120)
      const endX = rand(-20, 15)
      const midX = (startX + endX) / 2 + rand(-6, 6)

      const startY = rand(85, 115)
      const endY = rand(-15, 10)
      const midY = (startY + endY) / 2 + rand(-4, 4)

      const duration = rand(8, 12)
      // Offset clouds by half a cycle so one is arriving as the other leaves.
      const delay = -(i / glowCount) * duration

      return {
        key: i,
        xs: [`${startX}%`, `${midX}%`, `${endX}%`],
        ys: [`${startY}%`, `${midY}%`, `${endY}%`],
        duration,
        delay,
      }
    })
  }, [glowCount, id])

  return (
    <svg
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 size-full fill-neutral-400/80",
        className,
      )}
      {...props}
    >
      <defs>
        <pattern
          id={id}
          width={width}
          height={height}
          patternUnits="userSpaceOnUse"
          patternContentUnits="userSpaceOnUse"
          x={x}
          y={y}
        >
          <circle cx={cx} cy={cy} r={cr} />
        </pattern>

        {glow ? (
          <>
            {/* Very soft falloff — no visible ring edge, dots dissolve gradually. */}
            <radialGradient id={gradId}>
              <stop offset="0%"   stopColor="white" stopOpacity="0.9" />
              <stop offset="40%"  stopColor="white" stopOpacity="0.55" />
              <stop offset="75%"  stopColor="white" stopOpacity="0.18" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </radialGradient>

            <mask id={maskId}>
              <LazyMotion features={domAnimation}>
                {clouds.map((c) => (
                  <m.circle
                    key={`cloud-${c.key}`}
                    r={glowRadius}
                    fill={`url(#${gradId})`}
                    initial={{ cx: c.xs[0], cy: c.ys[0], opacity: 0 }}
                    animate={{
                      cx: c.xs,
                      cy: c.ys,
                      // Long hold with soft fade at both ends.
                      opacity: [0, 1, 1, 0],
                    }}
                    transition={{
                      duration: c.duration,
                      delay: c.delay,
                      repeat: Infinity,
                      ease: "linear",
                      times: [0, 0.3, 0.7, 1],
                    }}
                  />
                ))}
              </LazyMotion>
            </mask>
          </>
        ) : null}
      </defs>

      {/* Dot grid — only visible inside the drifting soft clouds when glow is on. */}
      <rect
        width="100%"
        height="100%"
        strokeWidth={0}
        fill={`url(#${id})`}
        mask={glow ? `url(#${maskId})` : undefined}
      />
    </svg>
  )
}
